import { useInfiniteQuery } from '@tanstack/react-query'
import { Inbox, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Skeleton } from '../../components/ui/skeleton'
import { api } from '../../lib/api'
import { cn } from '../../lib/cn'
import { addressLabel, formatConversationTime, messagePreview } from '../../lib/format'
import type { Conversation } from '../../types'

function ConversationRow({
  conversation,
  selected,
}: {
  conversation: Conversation
  selected: boolean
}) {
  const initials = conversation.contact.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
  return (
    <Link
      to={`/messages/${conversation.id}`}
      className={cn(
        'group flex min-h-[86px] w-full min-w-0 gap-3 overflow-hidden border-b border-[var(--border)] px-4 py-3.5 transition',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ring)]',
        selected ? 'bg-[var(--primary-soft)]' : 'hover:bg-[var(--surface-elevated)]',
      )}
    >
      <div className="relative mt-0.5 grid size-11 shrink-0 place-items-center rounded-2xl bg-[var(--muted)] text-sm font-bold text-[var(--foreground)]">
        {initials || 'WA'}
        {conversation.unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 size-3 rounded-full border-2 border-[var(--surface)] bg-[var(--primary)]" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold">
            {conversation.contact.displayName}
          </h2>
          <time className="shrink-0 pt-0.5 text-xs text-[var(--muted-foreground)]">
            {formatConversationTime(conversation.lastMessageAt)}
          </time>
        </div>
        <p className="mt-0.5 truncate text-xs text-[var(--muted-foreground)]">
          {addressLabel(conversation)}
        </p>
        <div className="mt-1 flex items-center gap-2">
          <p
            className={cn(
              'min-w-0 flex-1 truncate text-sm text-[var(--muted-foreground)]',
              conversation.unreadCount > 0 && 'font-semibold text-[var(--foreground)]',
            )}
          >
            {conversation.lastMessage?.direction === 'outgoing' && 'Tu: '}
            {messagePreview(conversation.lastMessage)}
          </p>
          {conversation.unreadCount > 0 && (
            <Badge className="min-w-6 justify-center bg-[var(--primary)] px-2 text-[var(--primary-foreground)]">
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </Badge>
          )}
        </div>
      </div>
    </Link>
  )
}

export function ConversationList({ selectedConversationId }: { selectedConversationId?: string }) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const query = useInfiniteQuery({
    queryKey: ['conversations', debouncedSearch, unreadOnly],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.conversations({ cursor: pageParam, query: debouncedSearch, unreadOnly }),
    getNextPageParam: (page) => page.nextCursor,
  })
  const conversations = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  )

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-[var(--border)] px-4 pb-4 pt-5 sm:px-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--primary)]">
              Inbox
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">Conversazioni</h1>
          </div>
          <Badge>{conversations.length}</Badge>
        </div>
        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[var(--muted-foreground)]" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cerca nome o numero"
            aria-label="Cerca conversazioni"
            className="pl-10"
          />
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            variant={!unreadOnly ? 'primary' : 'secondary'}
            onClick={() => setUnreadOnly(false)}
          >
            Tutte
          </Button>
          <Button
            size="sm"
            variant={unreadOnly ? 'primary' : 'secondary'}
            onClick={() => setUnreadOnly(true)}
          >
            Non lette
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto" aria-live="polite">
        {query.isLoading && (
          <div className="flex flex-col gap-1 p-2">
            {Array.from({ length: 7 }, (_, index) => (
              <div key={index} className="flex gap-3 rounded-2xl p-3">
                <Skeleton className="size-11 shrink-0 rounded-2xl" />
                <div className="flex flex-1 flex-col gap-2 pt-1">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-5/6" />
                </div>
              </div>
            ))}
          </div>
        )}
        {query.isError && (
          <div className="p-6 text-center">
            <p className="font-semibold">Impossibile caricare le conversazioni</p>
            <p className="mt-2 text-sm text-[var(--muted-foreground)]">
              Controlla la connessione e riprova.
            </p>
            <Button className="mt-4" variant="secondary" onClick={() => void query.refetch()}>
              Riprova
            </Button>
          </div>
        )}
        {!query.isLoading && !query.isError && conversations.length === 0 && (
          <div className="flex flex-col items-center px-8 py-16 text-center">
            <div className="grid size-14 place-items-center rounded-2xl bg-[var(--muted)] text-[var(--muted-foreground)]">
              <Inbox className="size-6" />
            </div>
            <h2 className="mt-5 text-base font-semibold">
              {search || unreadOnly ? 'Nessun risultato' : 'Nessun messaggio ancora'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[var(--muted-foreground)]">
              {search || unreadOnly
                ? 'Prova a modificare ricerca o filtri.'
                : 'Le nuove conversazioni Dualhook appariranno automaticamente qui.'}
            </p>
          </div>
        )}
        {conversations.map((conversation) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            selected={selectedConversationId === conversation.id}
          />
        ))}
        {query.hasNextPage && (
          <div className="p-4 text-center">
            <Button
              variant="secondary"
              size="sm"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? 'Caricamento…' : 'Carica altre'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
